import axios, {type AxiosRequestConfigWithParams} from '@core/services/axios';
import {AxiosHeaders} from 'axios';
import {Api} from '@common/constants';
import store from '@store';

// Helper to get tenantId from store
const getTenantId = () => store.getState().auth?.user?.tenantId;
const getUserId = () => store.getState().auth?.user?.id;
const getUserRole = () => store.getState().auth?.user?.role;
// List workflows
export const fetchRulesApi = async (params: {
  status: string;
  page: number;
  limit: number;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
  hierarchyKey?: string | null;
  options?: {skipTopLoader?: boolean};
}) => {
  const {
    status,
    page,
    limit,
    sortBy = 'updatedAt',
    sortDirection = 'DESC',
    search = '',
    hierarchyKey = null,
    options,
  } = params;
  const tenantId = getTenantId();
  const queryParams: Record<string, string | number> = {
    status: status.toUpperCase(),
    page,
    size: limit,
    sortBy,
    sortDirection,
    search,
  };
  // Only filter by hierarchy when a key is selected; omit otherwise so all workflows return.
  if (hierarchyKey) {
    queryParams.hierarchyId = hierarchyKey;
  }
  const requestConfig: AxiosRequestConfigWithParams = {
    headers: new AxiosHeaders({'X-Tenant-ID': tenantId ?? ''}),
    params: queryParams,
    skipTopLoader: options?.skipTopLoader,
  };
  const response = await axios.get(
    `${Api.RULE_ENGINE_BASE_URL}/bff${Api.WORKFLOWS}/filter`,
    requestConfig
  );
  return response.data.data; // Return response.data.data to get the actual content and statusCounts
};

// Create workflow
export const createRuleApi = async (data: {
  name: string;
  description: string;
  authEnabled?: boolean;
  hierarchyId?: string;
}) => {
  const tenantId = getTenantId();
  const response = await axios.post(
    `${Api.RULE_ENGINE_BASE_URL}${Api.WORKFLOWS}`,
    data,
    {
      headers: {'X-Tenant-ID': tenantId},
    }
  );
  return response.data;
};

// Edit workflow
export const editRuleApi = async (
  workflowId: string,
  data: {
    name: string;
    description: string;
    authEnabled?: boolean;
    hierarchyId?: string;
  }
) => {
  const tenantId = getTenantId();
  const response = await axios.put(
    `${Api.RULE_ENGINE_BASE_URL}${Api.WORKFLOWS}/${workflowId}`,
    data,
    {
      headers: {'X-Tenant-ID': tenantId},
    }
  );
  return response.data;
};

// Patch workflow action (submit for review, approve, reject, generate draft, discard draft)
export const patchWorkflowAction = async (
  workflowId: string,
  action:
    | 'REVIEW_DRAFT'
    | 'APPROVE_DRAFT'
    | 'REJECT_DRAFT'
    | 'CREATE_DRAFT'
    | 'DISCARD_DRAFT'
    | 'WITHDRAW_DRAFT'
) => {
  // Use getTenantId from this file (store-based)
  const tenantId = getTenantId();
  const response = await axios.patch(
    `${Api.RULE_ENGINE_BASE_URL}${Api.WORKFLOWS}/${workflowId}`,
    {action},
    {headers: {'X-Tenant-ID': tenantId}}
  );
  return response.data;
};

export const copyWorkflowApi = async (
  workflowId: string,
  status: string,
  data: {name: string; description: string}
) => {
  const tenantId = getTenantId();
  const response = await axios.post(
    `${Api.RULE_ENGINE_BASE_URL}${Api.WORKFLOWS}/${workflowId}/copy`,
    data,
    {
      headers: {'X-Tenant-ID': tenantId},
      params: {
        status: status?.toUpperCase(),
      },
    }
  );
  return response.data;
};

// get hierarchy list

export const getHierarchyListApi = async () => {
  const tenantId = getTenantId();
  const userId = getUserId();
  const userRole = getUserRole() ?? 'Admin';
  const response = await axios.get(
    `${Api.RULE_ENGINE_BASE_URL}/bff${Api.WORKFLOWS}/groups`,
    {
      headers: {
        'X-TENANT-ID': tenantId,
        'X-USER-ID': userId,
        'X-USER-ROLE': userRole,
      },
    }
  );
  return response.data;
};
